/** Spec 036 P11: folds a market stream's candles and per-indicator frames into the chart model and the readout row. */
import type { QuantChartModel as DrawModel } from '@/ui/lib/quantChart/types'
import type { QuantReadoutCell } from '@/ui/QuantReadout'

import { change24hPct, TF_SECONDS } from './candleStep'
import type { ManualIndicator } from './fixtures/indicators.fixture'
import { buildPreviewModel } from './previewModel'
import { baseFor } from './symbolBase'
import type { CandlePoint, IndicatorOverlay, PreviewOverlay } from './types'
import type { ProvisionalEntry } from './viewProtocol'

export interface IndicatorFrameState {
  rev: number
  last: number | null
  overlay: IndicatorOverlay
}

/** Overlays of every active indicator that has a frame. `show` false (computing) draws candles only. */
export function buildMarketModel(
  candles: CandlePoint[],
  symbol: string,
  active: ManualIndicator[],
  frames: Record<string, IndicatorFrameState>,
  show: boolean,
): DrawModel {
  if (!show) return buildPreviewModel(candles, null, baseFor(symbol).dp)
  const merged: PreviewOverlay = { rev: 0, legend: [], stats: [], lines: [], zones: [], levels: [], markers: [], sessions: [] }
  for (const ind of active) {
    const frame = frames[ind.id]
    if (frame === undefined) continue
    merged.legend.push(ind.name)
    merged.lines.push(...frame.overlay.lines)
    merged.zones.push(...frame.overlay.zones)
    merged.levels.push(...frame.overlay.levels)
    merged.markers.push(...frame.overlay.markers)
    merged.sessions.push(...frame.overlay.sessions)
  }
  return buildPreviewModel(candles, merged, baseFor(symbol).dp)
}

/** `+0.52%` with a true minus (U+2212). */
export function formatChange(pct: number): string {
  return `${pct >= 0 ? '+' : '−'}${Math.abs(pct).toFixed(2)}%`
}

/** The value of a time-keyed overlay line at `time`, or `null` when the line has no point there (warm-up, or no such line). */
function lineValueAt(frame: IndicatorFrameState, lineId: string, time: number): number | null {
  const line = frame.overlay.lines.find((l) => l.id === lineId)
  if (line === undefined) return null
  let lo = 0
  let hi = line.points.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const p = line.points[mid]
    if (p.time === time) return p.value
    if (p.time < time) lo = mid + 1
    else hi = mid - 1
  }
  return null
}

/**
 * O H L C, 24h %, then one value per active indicator that has a readout (`SMA 20`, `Pivot`, `BB 20 · 2 ↑`, `Signals`).
 *
 * `hoverIndex` (spec 038 R9): the candle under the crosshair. O H L C and the line indicators (SMA, EMA,
 * Bollinger upper) then show that bar's values; Pivot, Signals and `24h %` always stay at the latest values.
 * `null`, `undefined` or an out-of-range index means the latest bar.
 *
 * `provisional` (spec 070): line values at the forming bar. A line indicator's latest cell shows the
 * provisional value while a bar is forming; detections (Pivot, Signals) never take it, they change
 * only on a closed tick.
 */
export function buildMarketCells(
  candles: CandlePoint[],
  symbol: string,
  tf: string,
  active: ManualIndicator[],
  frames: Record<string, IndicatorFrameState>,
  showIndicators: boolean,
  hoverIndex?: number | null,
  provisional?: Record<string, ProvisionalEntry>,
): QuantReadoutCell[] {
  const latest = candles[candles.length - 1]
  if (latest === undefined) return []
  const at = typeof hoverIndex === 'number' && Number.isInteger(hoverIndex) ? candles[hoverIndex] : undefined
  const hovered = at !== undefined
  const last = at ?? latest
  const dp = baseFor(symbol).dp
  const f = (v: number): string => v.toFixed(dp)
  const change = change24hPct(candles, TF_SECONDS[tf] ?? 300)
  const cells: QuantReadoutCell[] = [
    { label: 'O', value: f(last.open) },
    { label: 'H', value: f(last.high) },
    { label: 'L', value: f(last.low) },
    { label: 'C', value: f(last.close), tone: 'accent' },
  ]
  if (change !== null) cells.push({ label: '24h %', value: formatChange(change), tone: change >= 0 ? 'up' : 'down' })
  if (!showIndicators) return cells
  for (const ind of active) {
    const frame = frames[ind.id]
    if (frame === undefined) continue
    const v = frame.last
    const prov = hovered ? undefined : provisional?.[ind.id]
    if (ind.type === 'sma' || ind.type === 'ema') {
      const val = hovered ? lineValueAt(frame, ind.id, last.time) : prov !== undefined ? prov.last : v
      cells.push({ label: ind.name, value: val === null ? '—' : f(val) })
    } else if (ind.type === 'bb') {
      const provUpper = prov?.points.find((p) => p.line === `${ind.id}:upper`)?.value
      const val = hovered ? lineValueAt(frame, `${ind.id}:upper`, last.time) : provUpper !== undefined ? provUpper : v
      cells.push({ label: `${ind.name} ↑`, value: val === null ? '—' : f(val) })
    } else if (ind.type === 'pivots') cells.push({ label: 'Pivot', value: v === null ? '—' : f(v) })
    else if (ind.type === 'ifvg') cells.push({ label: 'Signals', value: v === null ? '—' : String(v) })
  }
  return cells
}

/** The muted endpoint text under the chart. */
export function marketEndpoint(symbol: string, tf: string, active: ManualIndicator[]): string {
  return `ws /api/sandbox/view · market ${symbol} ${tf} · ind=${active.map((i) => i.type).join(',')}`
}
